import { useState, useCallback } from "react";

import { ListChecks, Check, ChevronDown, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button-shadcn";
import { GRANTS } from "@/other/constants/PrivilegeTypes";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { SearchTextLink } from "../SearchTextLink";
import { ExtractedChecklist } from "../types";

interface ChecklistExtractionResultsProps {
  checklists: ExtractedChecklist[];
}

interface ChecklistCardState {
  expanded: boolean;
  selectedItems: Set<number>;
  created: boolean;
  processing: boolean;
}

export const ChecklistExtractionResults = ({ checklists }: ChecklistExtractionResultsProps) => {
  const { project, entityRecord } = useFileDetailModal();

  const [cardStates, setCardStates] = useState<Record<number, ChecklistCardState>>(() => {
    const initial: Record<number, ChecklistCardState> = {};
    checklists.forEach((checklist, index) => {
      initial[index] = {
        expanded: index === 0,
        selectedItems: new Set(checklist.items.map((_, i) => i)),
        created: false,
        processing: false,
      };
    });
    return initial;
  });

  const updateCardState = useCallback((index: number, update: Partial<ChecklistCardState>) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], ...update },
    }));
  }, []);

  const toggleExpanded = useCallback((index: number) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], expanded: !prev[index].expanded },
    }));
  }, []);

  const toggleItemSelection = useCallback((checklistIndex: number, itemIndex: number) => {
    setCardStates((prev) => {
      const state = prev[checklistIndex];
      const newSelected = new Set(state.selectedItems);
      if (newSelected.has(itemIndex)) {
        newSelected.delete(itemIndex);
      } else {
        newSelected.add(itemIndex);
      }
      return {
        ...prev,
        [checklistIndex]: { ...state, selectedItems: newSelected },
      };
    });
  }, []);

  const handleCreateChecklist = useCallback(async (checklist: ExtractedChecklist, checklistIndex: number) => {
    if (!project || !entityRecord) return;

    const state = cardStates[checklistIndex];
    if (state?.created || state?.processing || state.selectedItems.size === 0) return;

    updateCardState(checklistIndex, { processing: true });

    try {
      const checklistStore = project.checklistStore;
      const checklistItemStore = project.checklistItemStore;
      if (!checklistStore || !checklistItemStore) {
        throw new Error("Checklist stores not available");
      }

      const lastChecklistOrder = checklistStore.last?.order ?? 0;
      const checklistRecord = checklistStore.createRecord({
        title: checklist.title || "Extracted Checklist",
        [entityRecord.entityType]: entityRecord.id,
        archived: (entityRecord as any).archived ?? false,
        canEdit: GRANTS.grant_five,
        canView: GRANTS.grant_six,
        order: lastChecklistOrder + 1,
        assignedBoardUsers: [],
      });

      checklistStore.add(checklistRecord);
      await project.sync();

      const selectedItems = checklist.items.filter((_, idx) => state.selectedItems.has(idx));
      let lastItemOrder = (checklistItemStore.last as any)?.order ?? 0;

      for (const item of selectedItems) {
        lastItemOrder += 1;
        const itemRecord = checklistItemStore.createRecord({
          description: item.description,
          checklistId: checklistRecord.id,
          archived: (entityRecord as any).archived ?? false,
          status: 1,
          order: lastItemOrder,
        });
        checklistItemStore.add(itemRecord);
      }

      await project.sync();

      updateCardState(checklistIndex, { created: true, processing: false });
      toast.success("Checklist Created", {
        description: `"${checklist.title || "Extracted Checklist"}" with ${selectedItems.length} item(s)`,
      });
    } catch (error) {
      console.error("[ChecklistExtractionResults] Failed to create checklist:", error);
      updateCardState(checklistIndex, { processing: false });
      toast.error("Failed to Create Checklist", {
        description: "Something went wrong. Please try again.",
      });
    }
  }, [project, entityRecord, cardStates, updateCardState]);

  if (checklists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <p className="text-sm text-muted-foreground">No checklists were extracted.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      {checklists.map((checklist, checklistIndex) => {
        const state = cardStates[checklistIndex];
        if (!state) return null;

        const selectedCount = state.selectedItems.size;

        return (
          <div
            key={checklistIndex}
            className={cn(
              "border border-border rounded-swivu-base overflow-hidden",
              state.created && "border-green-500/30 bg-green-500/5"
            )}
          >
            {/* Checklist header */}
            <button
              type="button"
              onClick={() => toggleExpanded(checklistIndex)}
              className="flex items-center gap-2 w-full px-3 py-2.5 hover:bg-muted/50 transition-colors"
            >
              {state.expanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              )}
              <span className="text-sm font-medium text-foreground truncate flex-1 text-left">
                {checklist.title || "Untitled Checklist"}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {checklist.items.length} item{checklist.items.length !== 1 ? "s" : ""}
              </span>
              {state.created && (
                <Check className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
              )}
            </button>

            {/* Expandable items section */}
            <AnimatePresence>
              {state.expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-border">
                    {/* Individual items */}
                    <div className="flex flex-col">
                      {checklist.items.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className={cn(
                            "flex items-start gap-2 px-3 py-2",
                            itemIndex < checklist.items.length - 1 && "border-b border-border/30"
                          )}
                        >
                          {!state.created && (
                            <input
                              type="checkbox"
                              checked={state.selectedItems.has(itemIndex)}
                              onChange={() => toggleItemSelection(checklistIndex, itemIndex)}
                              className="mt-0.5 rounded border-border accent-primary shrink-0"
                            />
                          )}
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className={cn(
                              "text-sm text-foreground",
                              state.created && !state.selectedItems.has(itemIndex) && "line-through text-muted-foreground"
                            )}>
                              {item.description}
                            </span>
                            {item.search_text && (
                              <div className="mt-1">
                                <SearchTextLink searchText={item.search_text} />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Create Checklist action */}
                    <div className="px-3 py-2 border-t border-border">
                      <Button
                        type="button"
                        variant={state.created ? "ghost" : "default"}
                        size="sm"
                        disabled={state.created || state.processing || selectedCount === 0}
                        onClick={() => handleCreateChecklist(checklist, checklistIndex)}
                        className={cn(
                          "w-full gap-2",
                          state.created && "text-green-600 dark:text-green-400"
                        )}
                      >
                        {state.created ? (
                          <>
                            <Check className="w-4 h-4" />
                            Checklist Created
                          </>
                        ) : (
                          <>
                            <ListChecks className="w-4 h-4" />
                            {state.processing
                              ? "Creating..."
                              : `Create Checklist (${selectedCount} item${selectedCount !== 1 ? "s" : ""})`}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
